import type { ParsedUrlQueryInput } from 'node:querystring';
import { colors } from '../../theme/default/color';
import Grid from '@mui/material/GridLegacy';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { styled, Theme } from '@mui/material/styles';
import TypoGraphy from '@mui/material/Typography';

import React, { useContext } from 'react';
import arrowRight from '../../assets/svg/icons/arrowRightBlue.svg';
import facebook from '../../assets/svg/icons/socialGray/facebook.svg';
import linkedin from '../../assets/svg/icons/socialGray/linkedin.svg';
import medium from '../../assets/svg/icons/socialGray/medium.svg';
import twitter from '../../assets/svg/icons/socialGray/twitter.svg';
import { formatDate } from '../../utils/formatDate';
import type { BlogSidebarProps } from './BlogSidebar.type';
import { NextLink } from '../NextLink';
import { NextComponentsContext } from '../../../context/nextComponentsContext/nextComponentsContext';
import { normalizeCategoryName } from './BlogSidebar.utils';

const PREFIX = 'BlogSidebar';

const classes = {
  root: `${PREFIX}-root`,
  categoryContainer: `${PREFIX}-categoryContainer`,
  sectionTitle: `${PREFIX}-sectionTitle`,
  categoryButton: `${PREFIX}-categoryButton`,
  categoryName: `${PREFIX}-categoryName`,
  line: `${PREFIX}-line`,
  pubDateText: `${PREFIX}-pubDateText`,
  imageLatest: `${PREFIX}-imageLatest`,
  articleTitle: `${PREFIX}-articleTitle`,
  socialList: `${PREFIX}-socialList`,
  socialListItem: `${PREFIX}-socialListItem`,
  socialIcon: `${PREFIX}-socialIcon`,
};

const StyledBlogSidebar = styled('div')(({ theme }: { theme: Theme }) => ({
  [`& .${classes.root}`]: {
    color: theme.palette.common.white,
  },

  [`& .${classes.categoryContainer}`]: {
    background: 'linear-gradient(37deg, #070E15, #141A21)',
    boxShadow:
      'rgba(0, 0, 0, 0.2) 0px 3px 5px -1px, rgba(0, 0, 0, 0.14) 0px 6px 10px 0px, rgba(0, 0, 0, 0.12) 0px 1px 18px 0px;',
    marginTop: '100px',
    fontSize: '22px',
    fontWeight: 'bold',
    textAlign: 'center',
    borderRadius: '1.5rem 0 0 1.5rem',
    padding: '40px 40px',
  },

  [`& .${classes.sectionTitle}`]: {
    fontSize: '22px',
    marginTop: '0',
    marginBottom: '40px',
    paddingTop: '0',
    paddingBottom: '8px',
  },

  [`& .${classes.categoryButton}`]: {
    backgroundColor: 'transparent',
    border: 'none',
  },

  [`& .${classes.categoryName}`]: {
    textDecoration: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: '16px',
    textTransform: 'capitalize',
    transition: '0.5s',
    color: 'white',
    '&>img': {
      opacity: '0',
      marginRight: '-10px',
    },
    '&:hover': {
      color: theme.palette.blueColors?.PLUS_1,
      '&>img': {
        transition: '0.5s',
        marginRight: '15px',
        opacity: '1',
      },
    },
  },

  [`& .${classes.line}`]: {
    border: 'solid 0.5px' + colors.lightgray5A,
  },

  [`& .${classes.pubDateText}`]: {
    fontSize: '14px',
    color: colors.mediumgray400,
  },

  [`& .${classes.imageLatest}`]: {
    width: '57px',
    height: '57px',
    objectFit: 'cover',
    borderRadius: '10px',
  },

  [`& .${classes.articleTitle}`]: {
    cursor: 'pointer',
    transition: '0.5s',
    color: theme.palette.common.white,
    '&:hover': {
      color: theme.palette.blueColors?.PLUS_1,
    },
  },

  [`& .${classes.socialList}`]: {
    display: 'flex',
  },

  [`& .${classes.socialListItem}`]: {
    padding: '0px',
    marginLeft: '10px',
  },

  [`& .${classes.socialIcon}`]: {
    '&:hover': {
      opacity: '0.8',
    },
  },
}));

export type QueryType = string | ParsedUrlQueryInput | null | undefined;

export const BlogSidebar: React.FC<BlogSidebarProps> = ({
  categoryList,
  latest,
}) => {
  const useRouter = useContext(NextComponentsContext)?.hooks?.useRouter;
  const router = useRouter ? useRouter() : null;

  const handleRedirect = (query: QueryType) => {
    if (useRouter) {
      router.push(
        {
          pathname: '/blog/category/[slug]',
          query,
        },
        undefined,
        { shallow: true }
      );
    } else {
      window.location.href = '/blog';
    }
  };
  return (
    <Grid
      item
      xs={12}
      md={5}
      sm={3}
      lg={3}
      xl={3}
      sx={{
        display: { xs: 'none', lg: 'block' },
      }}>
      <StyledBlogSidebar>
        <Grid item xs={12} className={classes.categoryContainer}>
          <TypoGraphy variant="overline" className={classes.sectionTitle}>
            Categories
          </TypoGraphy>
          {categoryList.map((category: string) => (
            <div key={category}>
              <Grid container alignItems="start" my={2}>
                <button
                  className={classes.categoryButton}
                  onClick={() => {
                    handleRedirect({
                      slug: normalizeCategoryName(category),
                    });
                  }}>
                  <TypoGraphy className={classes.categoryName} variant="h6">
                    <img src={arrowRight} alt="arrow" width="8" height="13" />
                    {category}
                  </TypoGraphy>
                </button>
              </Grid>
              <div className={classes.line} />
            </div>
          ))}
        </Grid>
        <Grid
          style={{ marginTop: '50px' }}
          item
          xs={12}
          className={classes.categoryContainer}>
          <TypoGraphy variant="overline" className={classes.sectionTitle}>
            Recent posts
          </TypoGraphy>

          {latest.map((article) => {
            const thumbnailIcon = article?.thumbnail
              ? article?.thumbnail?.url
              : article?.image?.url;
            return (
              <Grid
                key={article.documentId}
                container
                style={{ marginBottom: '30px' }}
                direction="row"
                justifyContent="space-between"
                alignItems="center">
                <Grid item xs={12}>
                  <TypoGraphy align="left" className={classes.pubDateText}>
                    {formatDate(article?.pubDate)}
                  </TypoGraphy>
                </Grid>
                <Grid item md={2}>
                  <img
                    className={classes.imageLatest}
                    src={thumbnailIcon}
                    alt="article miniature"
                    width="57"
                    height="57"
                  />
                </Grid>
                <Grid item md={8}>
                  <NextLink
                    sx={{ textDecoration: 'none' }}
                    href={'/blog/post/' + article?.slug + '/'}>
                    <TypoGraphy
                      variant="h6"
                      align="left"
                      className={classes.articleTitle}>
                      {article?.title.toString().slice(0, 35) + '...'}
                    </TypoGraphy>
                  </NextLink>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
        <Grid
          item
          style={{ marginTop: '50px' }}
          xs={12}
          className={classes.categoryContainer}>
          <TypoGraphy variant="overline" className={classes.sectionTitle}>
            Follow Us
          </TypoGraphy>
          <List className={classes.socialList}>
            {/* TODO remove duplicated code */}
            <ListItem className={classes.socialListItem}>
              <a
                href="https://www.facebook.com/rumblefishsoftwaredevelopment"
                target="_blank"
                rel="noreferrer">
                {' '}
                <img
                  className={classes.socialIcon}
                  src={facebook}
                  alt="Facebook"
                  width="35"
                  height="35"
                />
              </a>
            </ListItem>
            <ListItem className={classes.socialListItem}>
              <a
                href="https://www.linkedin.com/company/rumblefishdev"
                target="_blank"
                rel="noreferrer">
                {' '}
                <img
                  className={classes.socialIcon}
                  src={linkedin}
                  alt="LinkedIn"
                  width="35"
                  height="35"
                />
              </a>
            </ListItem>
            <ListItem className={classes.socialListItem}>
              <a
                href="https://medium.com/@rumblefishdev"
                target="_blank"
                rel="noreferrer">
                {' '}
                <img
                  className={classes.socialIcon}
                  src={medium}
                  alt="Medium"
                  width="35"
                  height="35"
                />
              </a>
            </ListItem>
            <ListItem className={classes.socialListItem}>
              <a
                href="https://twitter.com/rumblefishdev"
                target="_blank"
                rel="noreferrer">
                {' '}
                <img
                  className={classes.socialIcon}
                  src={twitter}
                  alt="Twitter"
                  width="35"
                  height="35"
                />
              </a>
            </ListItem>
          </List>
        </Grid>
      </StyledBlogSidebar>
    </Grid>
  );
};
